import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Container, Badge, Card, Alert } from 'react-bootstrap';
import axios from 'axios';
import NavigationBar from '../NavigationBar';

// Telegram alert rules sent through the Flow bot. Rules are created by an admin talking to Flow
// ("Remind Karim every morning about challans missing the factory photo…"); here they can be
// checked, previewed, sent once for testing, turned on/off or deleted.
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function AlertRules() {
    const [rules, setRules] = useState([]);
    const [sensors, setSensors] = useState([]);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState(null); // { name, text }
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        fetchRules();
    }, []);

    const fetchRules = async () => {
        try {
            const res = await axios.get(`${window.backendUrl}/alert-rules`, { withCredentials: true });
            setRules(res.data.rules);
            setSensors(res.data.sensors);
            setError('');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load alert rules.');
        }
    };

    const toggleEnabled = async (rule) => {
        try {
            await axios.put(`${window.backendUrl}/alert-rules/${rule.ruleId}/enabled`, { enabled: !rule.enabled }, { withCredentials: true });
            fetchRules();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update the rule');
        }
    };

    const showPreview = async (rule) => {
        setBusyId(rule.ruleId);
        try {
            const res = await axios.post(`${window.backendUrl}/alert-rules/${rule.ruleId}/preview`, {}, { withCredentials: true });
            setPreview({ name: rule.name, text: res.data.text || 'Nothing right now. No message is sent while the check finds nothing.' });
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to preview the rule');
        } finally {
            setBusyId(null);
        }
    };

    const sendNow = async (rule) => {
        if (!window.confirm(`Send "${rule.name}" now to its ${rule.reaches} recipient(s)?`)) return;
        setBusyId(rule.ruleId);
        try {
            const res = await axios.post(`${window.backendUrl}/alert-rules/${rule.ruleId}/send-now`, {}, { withCredentials: true });
            alert(res.data.sent ? `Sent to ${res.data.sent} person(s).` : 'Nothing was sent (the check found nothing, or nobody is linked to Telegram).');
            fetchRules();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to send the rule');
        } finally {
            setBusyId(null);
        }
    };

    const deleteRule = async (rule) => {
        if (!window.confirm(`Delete the alert "${rule.name}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${window.backendUrl}/alert-rules/${rule.ruleId}`, { withCredentials: true });
            fetchRules();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to delete the rule');
        }
    };

    const formatWhen = (when) => {
        if (when === 'instant') return 'Instantly';
        const days = when.days.length === 7 ? 'Every day' : when.days.map(d => DAY_NAMES[d]).join(', ');
        return `${when.times.join(', ')} · ${days}`;
    };

    const formatSettings = (settings) => Object.entries(settings || {})
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${k}: ${v}`).join(' · ');

    const formatDate = (value) => (value ? String(value).replace('T', ' ').slice(0, 16) : '—');

    return (
        <>
            <NavigationBar/>
            <Container fluid className="mt-4 mb-5 px-4">
                <h3>Alert Rules</h3>
                <p className="text-muted small mb-3">
                    Telegram alerts sent by the Flow bot. To create one, an admin tells Flow on Telegram, e.g.
                    <em> "Remind the Factory Production Msil department every morning at 10 about challans missing the factory photo for more than 2 days."</em>
                    {' '}Only people who tapped <strong>Connect Telegram</strong> in the mobile app (and pressed Start on Flow) receive alerts.
                </p>
                {error && <Alert variant="danger">{error}</Alert>}

                <Card className="shadow-sm mb-4">
                    <Card.Header><strong>Rules</strong></Card.Header>
                    <Table hover responsive className="mb-0 align-middle">
                        <thead className="bg-light">
                            <tr>
                                <th>Name</th>
                                <th>Check</th>
                                <th>Recipients</th>
                                <th>When</th>
                                <th>Last run</th>
                                <th className="text-center">On</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rules.length === 0 && !error && (
                                <tr><td colSpan={7} className="text-center text-muted py-4">No alert rules yet. Nobody gets alerts until an admin creates one through Flow.</td></tr>
                            )}
                            {rules.map(rule => (
                                <tr key={rule.ruleId} className={rule.enabled ? '' : 'text-muted'}>
                                    <td><strong>{rule.name}</strong></td>
                                    <td>
                                        <div>{rule.sensorTitle}</div>
                                        <div className="small text-muted">{formatSettings(rule.settings)}</div>
                                    </td>
                                    <td style={{ maxWidth: 320 }}>
                                        {rule.recipientUsers.map(u => <Badge key={u.userId} bg="secondary" className="me-1">{u.name}</Badge>)}
                                        {rule.recipientDepartments.map(d => <Badge key={d} bg="info" className="me-1">{d}</Badge>)}
                                        <div className="small text-muted">
                                            Reaches {rule.reaches}
                                            {rule.notLinkedToTelegram.length > 0 && (
                                                <span className="text-warning" title={rule.notLinkedToTelegram.join(', ')}> · {rule.notLinkedToTelegram.length} not on Telegram</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="small">{formatWhen(rule.when)}</td>
                                    <td className="small">
                                        {formatDate(rule.lastRunAt)}
                                        {rule.lastResultCount !== null && rule.lastResultCount !== undefined && <div className="text-muted">{rule.lastResultCount} found</div>}
                                    </td>
                                    <td className="text-center">
                                        <Form.Check type="switch" checked={rule.enabled} onChange={() => toggleEnabled(rule)} />
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <Button size="sm" variant="outline-primary" className="me-1" disabled={busyId === rule.ruleId} onClick={() => showPreview(rule)}>Preview</Button>
                                        {rule.when !== 'instant' && (
                                            <Button size="sm" variant="outline-success" className="me-1" disabled={busyId === rule.ruleId} onClick={() => sendNow(rule)}>Send now</Button>
                                        )}
                                        <Button size="sm" variant="outline-danger" onClick={() => deleteRule(rule)}>Delete</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>

                {sensors.length > 0 && (
                    <Card className="shadow-sm">
                        <Card.Header><strong>Available checks</strong> <span className="text-muted small ms-2">What Flow can watch. A new kind of check needs a developer.</span></Card.Header>
                        <Table responsive className="mb-0 small">
                            <tbody>
                                {sensors.map(s => (
                                    <tr key={s.sensor}>
                                        <td style={{ width: 220 }}><strong>{s.title}</strong>{s.when === 'instant' && <Badge bg="warning" text="dark" className="ms-2">instant</Badge>}</td>
                                        <td>
                                            <div>{s.about}</div>
                                            <div className="text-muted">
                                                {Object.entries(s.settings).map(([k, v]) => `${k}${v.default !== undefined ? ` (default ${v.default})` : ''}: ${v.about}`).join(' · ')}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                )}
            </Container>

            <Modal show={!!preview} onHide={() => setPreview(null)} centered>
                <Modal.Header closeButton><Modal.Title>{preview?.name}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p className="small text-muted mb-2">What it would send right now:</p>
                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', background: '#f6f8fa', padding: 12, borderRadius: 6 }}>{preview?.text}</pre>
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={() => setPreview(null)}>Close</Button></Modal.Footer>
            </Modal>
        </>
    );
}

export default AlertRules;
